/* eslint-disable no-nested-ternary */
import { useRef, useState } from "react";
import styles from "./TrustPilotBanner.module.scss";
import LazyImage from "@Components/Image";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE, TRUSTPILOT_REVIEWS_URL_CHINA } from "constants/configConstants";
import CustomLink from "@Components/CustomLink";
import useIsApp from "hooks/useIsApp";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import useIsLanguage from "hooks/useIsLanguage";
import { getRandomAvatar } from "@Pages/PropertyPage/components/ReviewsSectionV2/avatars";
import CountryFlag from "@Components/CountryFlag";
import { getNationalityCountryCode } from "@Pages/PropertyPage/components/ReviewsSectionV2/utils";
import { TrustpilotStars } from "@Components/TrustPilotCustomWidget";
import { TrustpilotReview } from "./helper";

type TPCarouselItem = {
  tp: TrustpilotReview;
  isHomePage?: boolean;
  isDesktop?: boolean;
  hideFlagBadge?: boolean;
  verbatim?: boolean;
};

const TPCarouselItem = ({
  tp,
  isHomePage = false,
  isDesktop = false,
  hideFlagBadge = false,
  verbatim = false,
}: TPCarouselItem) => {
  const { t } = useTranslationHook();
  const userLanguage = useIsLanguage();
  const isApp = useIsApp();
  const charLimit = 120;
  const ref = useRef<any>();
  const [expanded, setExpanded] = useState(false);

  const reviewText = verbatim
    ? tp?.trustpilot_review || ""
    : (tp as Record<string, any>)?.[`edited_review_${userLanguage}`] || tp?.edited_review || "";
  const userName = tp?.user_name || "";
  const rating = Number(tp?.trustpilot_rating);

  const getFlagCode = (): string => {
    const code = getNationalityCountryCode(tp.nationality || "");
    return code || "";
  };

  const toggleExpanded = () => {
    gtagEventEmitter({
      action: expanded
        ? "trustpilot_review_subsection_read_less_clicked"
        : "trustpilot_review_subsection_read_more_clicked",
      category: PAGE.SEARCH,
    });
    setExpanded(!expanded);
  };

  const onStarsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    gtagEventEmitter({
      action: "trustpilot_link_clicked",
      category: isHomePage ? PAGE.HOME : PAGE.SEARCH,
      platform: "web",
      surface: "review_card",
      language: userLanguage,
      method: "open",
    });
    if (isApp) {
      event.preventDefault();
      sendWebViewEvent(WEB_VIEW_APP_EVENTS.OPEN_EXTERNAL_URL, {
        url: TRUSTPILOT_REVIEWS_URL_CHINA,
      });
    }
  };

  const onKeyDown = (event: any) => {
    if (event.key === "Enter") {
      toggleExpanded();
    }
  };

  return (
    <div
      className={formatClassNames(
        styles.trustPilotCarouselItem,
        isHomePage && isDesktop && styles.trustpilotHomeItem,
        !isHomePage && styles.addPadding,
      )}
      data-testid="homepage-tp-review-carousel-item"
    >
      <div className={styles.carouselItemBorder}>
        <div className={styles.readMoreContainer}>
          <div
            ref={ref}
            className={`${styles.trustPilotCarouselReviewText} ${expanded ? styles.expanded : ""}`}
            data-testid="homepage-tp-customer-review"
          >
            {reviewText?.length > charLimit ? (
              <>
                {expanded ? (
                  <>
                    {reviewText}
                    <span
                      onClick={toggleExpanded}
                      className={styles.read}
                      role="button"
                      onKeyDown={onKeyDown}
                      tabIndex={0}
                      data-testid="Read Less"
                    >
                      &nbsp; {t("Read Less")}
                    </span>
                  </>
                ) : (
                  <>
                    {reviewText.substr(0, charLimit)}
                    .....
                    <span
                      className={styles.read}
                      onClick={toggleExpanded}
                      role="button"
                      onKeyDown={onKeyDown}
                      tabIndex={0}
                      data-testid="Read More"
                    >
                      {t("Read More")}
                    </span>
                  </>
                )}
              </>
            ) : (
              reviewText
            )}
          </div>
        </div>
        {verbatim ? (
          <div className={styles.trustPilotBottom}>
            <div className={styles.trustPilotMeta}>
              <div className={styles.trustPilotUserFull} data-testid="homepage-tp-user-name">
                {userName}
              </div>
              {rating >= 1 && rating <= 5 && (
                <CustomLink
                  href={TRUSTPILOT_REVIEWS_URL_CHINA}
                  target="_blank"
                  rel="noopener noreferrer"
                  isExternal
                  className={styles.reviewStarsLink}
                  dataTestId="tp-card-stars-link"
                  aria-label={t("home.trustpilotreviewsbanner.read_on_trustpilot")}
                  onClick={onStarsClick}
                >
                  <TrustpilotStars score={rating} className={styles.reviewStars} />
                </CustomLink>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.trustPilotBottom}>
            <div className={styles.avatarContainer}>
              <LazyImage
                height={42}
                width={42}
                src={getRandomAvatar(tp?.user_name || "", 0)}
                className={styles.avatarImage}
                alt={`${tp?.user_name || "User"} avatar`}
              />
              {!hideFlagBadge && (
                <div className={styles.flagBadge}>
                  <CountryFlag
                    countryCode={getFlagCode()}
                    width={16}
                    height={16}
                    className={styles.flagImage}
                  />
                </div>
              )}
            </div>
            <div className={styles.trustPilotMeta}>
              <div className={styles.trustPilotUser} data-testid="homepage-tp-user-name">
                {userName.length > 13 ? userName.split(" ")[0] : tp?.user_name}
              </div>
              <div
                className={isHomePage ? styles.trustPilotUniHome : styles.trustPilotUni}
                data-testid="homepage-tp-university-name"
              >
                {tp?.university}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(TPCarouselItem, {
  componentName: "TPCarouselItem-SearchListDesktop-SearchPage",
  showForChina: true,
});
